import { PHYSICS_CONFIG, PLAYER_CONFIG } from '@/config/constants';

/**
 * AI Player - Controls the game character during demo/attract mode
 * Simulates human-like gameplay with imperfection for natural appearance
 */
export interface AIInput {
  left: boolean;
  right: boolean;
  shoot: boolean;
}

export class AIPlayer {
  private targetX: number = PLAYER_CONFIG.START_X;
  private shootCooldown: number = 0;
  private reactionDelay: number = 0;
  private moveThreshold: number = 40; // How close is "close enough" to target
  private lastDecisionTime: number = 0;
  private decisionInterval: number = 200; // Re-evaluate every 200ms

  /**
   * Get AI input decisions based on current game state
   */
  getInput(
    playerX: number,
    playerY: number,
    balls: { x: number; y: number; radius: number; vx: number; vy: number }[],
    delta: number
  ): AIInput {
    const input: AIInput = { left: false, right: false, shoot: false };

    // Update cooldowns
    this.shootCooldown = Math.max(0, this.shootCooldown - delta);
    this.reactionDelay = Math.max(0, this.reactionDelay - delta);
    this.lastDecisionTime += delta;

    if (this.reactionDelay > 0) return input;

    // Re-evaluate target periodically
    if (this.lastDecisionTime >= this.decisionInterval) {
      this.lastDecisionTime = 0;
      this.evaluateTarget(playerX, playerY, balls);
    }

    // Move toward target
    const dx = this.targetX - playerX;
    if (Math.abs(dx) > this.moveThreshold) {
      if (dx < 0) {
        input.left = true;
      } else {
        input.right = true;
      }
    }

    // Shoot when roughly aligned with a ball above
    if (this.shootCooldown <= 0) {
      const alignedBall = this.findAlignedBall(playerX, playerY, balls);
      if (alignedBall) {
        input.shoot = true;
        // Add realistic cooldown with randomness (500-1200ms)
        this.shootCooldown = 500 + Math.random() * 700;
        // Add slight reaction delay after shooting (100-300ms)
        this.reactionDelay = 100 + Math.random() * 200;
      }
    }

    return input;
  }

  /**
   * Evaluate and set the best target position
   */
  private evaluateTarget(
    playerX: number,
    playerY: number,
    balls: { x: number; y: number; radius: number; vx: number; vy: number }[]
  ): void {
    if (balls.length === 0) {
      // No balls, wander slightly
      this.targetX = PLAYER_CONFIG.START_X + (Math.random() - 0.5) * 400;
      return;
    }

    // Check for danger first - balls about to hit player
    const dangerBall = this.findDangerousBall(playerX, playerY, balls);
    if (dangerBall) {
      // Dodge: move away from the ball
      if (dangerBall.x < playerX) {
        this.targetX = Math.min(PHYSICS_CONFIG.WALL_RIGHT - 100, playerX + 200);
      } else {
        this.targetX = Math.max(PHYSICS_CONFIG.WALL_LEFT + 100, playerX - 200);
      }
      return;
    }

    // Find the best ball to target (prefer lower, descending balls)
    let bestBall = balls[0];
    let bestScore = -Infinity;

    for (const ball of balls) {
      // Score based on: lower is better, descending is better, closer is slightly better
      let score = ball.y * 2; // Prefer lower balls
      if (ball.vy > 0) score += 200; // Prefer descending balls
      score -= Math.abs(ball.x - playerX) * 0.3; // Slight preference for closer
      // Prefer smaller balls (easier to pop for points)
      score += (128 - ball.radius) * 0.5;

      if (score > bestScore) {
        bestScore = score;
        bestBall = ball;
      }
    }

    // Predict where ball will be when we can shoot
    const predictedX = bestBall.x + bestBall.vx * 0.3; // 300ms prediction

    // Add slight imperfection (offset by 0-30px)
    this.targetX = predictedX + (Math.random() - 0.5) * 60;

    // Clamp to game boundaries
    this.targetX = Math.max(
      PHYSICS_CONFIG.WALL_LEFT + 80,
      Math.min(PHYSICS_CONFIG.WALL_RIGHT - 80, this.targetX)
    );
  }

  /**
   * Find a ball that's dangerously close to the player
   */
  private findDangerousBall(
    playerX: number,
    playerY: number,
    balls: { x: number; y: number; radius: number; vx: number; vy: number }[]
  ): { x: number; y: number; vx: number } | null {
    const dangerRadius = 150; // Distance threshold for "danger"

    for (const ball of balls) {
      const dx = ball.x - playerX;
      const dy = ball.y - playerY;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Ball is close and moving toward player
      if (distance < dangerRadius + ball.radius && ball.vy > 0 && dy < 0) {
        return ball;
      }
    }
    return null;
  }

  /**
   * Find a ball that's roughly above the player for shooting
   */
  private findAlignedBall(
    playerX: number,
    playerY: number,
    balls: { x: number; y: number; radius: number; vx: number; vy: number }[]
  ): { x: number; y: number } | null {
    const alignmentThreshold = 80; // How precisely aligned we need to be

    for (const ball of balls) {
      // Ball must be above player
      if (ball.y >= playerY) continue;

      const dx = Math.abs(ball.x - playerX);
      if (dx < alignmentThreshold + ball.radius * 0.5) {
        return ball;
      }
    }
    return null;
  }

  /**
   * Reset AI state
   */
  reset(): void {
    this.targetX = PLAYER_CONFIG.START_X;
    this.shootCooldown = 0;
    this.reactionDelay = 0;
    this.lastDecisionTime = 0;
  }
}
